/**
 * Which problems the editor shows. A new flow should not open covered in red: a problem shows
 * once the person has touched that part of the flow — changed a value that was there, or left
 * the section or step it is in — and every problem shows after a save or test attempt.
 *
 * Keys are the validation locations with steps named by id instead of position
 * ("steps.1.with.to" → "step:notify.with.to"), so moving a step keeps what was touched.
 * A key covers everything below it: "step:notify" shows every problem of that step.
 */
import { allSteps, type FlowDef, type Step } from './definition';
import type { Issue } from './validate';

export interface Touched {
  keys: ReadonlySet<string>;
  /** A save or test attempt was made: every problem shows, for the rest of the editing session. */
  revealed: boolean;
}

/** The parts a flow already has. A saved flow (or a copy of one, or an AI draft to review) shows their problems at once. */
export function existingParts(def: FlowDef): string[] {
  return ['', 'name', 'trigger', 'target', 'conditions', 'steps', 'phases', 'cooldown', ...allSteps(def).map((s) => `step:${s.id}`)];
}

export function startTouched(def: FlowDef, existing: boolean): Touched {
  return { keys: new Set(existing ? existingParts(def) : []), revealed: false };
}

/** The stable key of a validation location. */
export function issueKey(def: FlowDef, loc: string): string {
  if (loc === 'steps' || loc === 'phases') return 'steps';
  const m = /^(?:phases\.(\d+)\.)?steps\.(\d+)(\..*)?$/.exec(loc);
  if (m) {
    const list = m[1] !== undefined ? def.phases[Number(m[1])]?.steps : def.steps;
    const step = list?.[Number(m[2])];
    if (step) return `step:${step.id}${m[3] ?? ''}`;
  }
  return loc;
}

/** Whether `key` or a part above it was touched. */
export function covers(keys: ReadonlySet<string>, key: string): boolean {
  if (keys.has(key)) return true;
  for (let i = key.indexOf('.'); i !== -1; i = key.indexOf('.', i + 1)) if (keys.has(key.slice(0, i))) return true;
  return false;
}

/** The problems to show now. */
export function shownIssues(issues: Issue[], def: FlowDef, touched: Touched): Issue[] {
  return touched.revealed ? issues : issues.filter((i) => covers(touched.keys, issueKey(def, i.loc)));
}

export function touch(touched: Touched, key: string): Touched {
  return touched.keys.has(key) ? touched : { ...touched, keys: new Set([...touched.keys, key]) };
}

export function reveal(touched: Touched): Touched {
  return touched.revealed ? touched : { ...touched, revealed: true };
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const blank = (v: unknown) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0) || (isObject(v) && Object.keys(v).length === 0);

/**
 * The locations whose value changed. Filling in something that was empty does not count (a new
 * step, a new condition, the first letters of a name are not finished yet); changing or clearing
 * a value does. Lists of objects (conditions) are compared row by row.
 */
function changedPaths(before: unknown, after: unknown, path: string, out: string[]): void {
  if (before === after || blank(before) || JSON.stringify(before) === JSON.stringify(after)) return;
  if (isObject(before) && isObject(after)) {
    for (const k of new Set([...Object.keys(before), ...Object.keys(after)])) changedPaths(before[k], after[k], `${path}.${k}`, out);
    return;
  }
  if (Array.isArray(before) && Array.isArray(after) && before.some(isObject)) {
    for (let i = 0; i < Math.max(before.length, after.length); i++) changedPaths(before[i], after[i], `${path}.${i}`, out);
    if (!after.length) out.push(path);
    return;
  }
  out.push(path);
}

const body = ({ id: _id, ...rest }: Step) => rest;
const under = (key: string, prefix: string) => key === prefix || key.startsWith(`${prefix}.`);

/** After an edit: what changed counts as touched; a renamed step keeps its keys, a removed one loses them. */
export function afterEdit(touched: Touched, before: { name: string; def: FlowDef }, after: { name: string; def: FlowDef }): Touched {
  const out: string[] = [];
  if (before.name !== after.name && before.name.trim()) out.push('name');
  let keys = [...touched.keys];
  if (before.def !== after.def) {
    for (const part of ['trigger', 'target', 'conditions', 'cooldown'] as const) changedPaths(before.def[part], after.def[part], part, out);
    const was = new Map(allSteps(before.def).map((s) => [s.id, s]));
    const now = new Map(allSteps(after.def).map((s) => [s.id, s]));
    const gone = [...was.keys()].filter((id) => !now.has(id));
    const added = [...now.keys()].filter((id) => !was.has(id));
    const [from] = gone;
    const [to] = added;
    if (gone.length === 1 && added.length === 1 && was.size === now.size && from !== undefined && to !== undefined) {
      // A rename (the reference changed): the step keeps what was touched in it.
      keys = keys.map((k) => (under(k, `step:${from}`) ? `step:${to}${k.slice(`step:${from}`.length)}` : k));
      const step = was.get(from);
      if (step) was.set(to, step);
    } else {
      keys = keys.filter((k) => !gone.some((id) => under(k, `step:${id}`)));
    }
    for (const [id, step] of now) {
      const old = was.get(id);
      if (old) changedPaths(body(old), body(step), `step:${id}`, out);
    }
    if (was.size > 0 && now.size === 0) out.push('steps');
  }
  const next = new Set([...keys, ...out]);
  return next.size === touched.keys.size && [...next].every((k) => touched.keys.has(k)) ? touched : { ...touched, keys: next };
}

/** Problems in the order of the page: about, when, for, only if, the steps in order, limits. */
export function pageOrder(def: FlowDef, issues: Issue[]): Issue[] {
  const index = new Map(allSteps(def).map((s, i) => [s.id, i]));
  const fixed: Record<string, number> = { name: 0, trigger: 1, target: 2, conditions: 3, steps: 4, phases: 5 };
  const rank = (loc: string): number => {
    const key = issueKey(def, loc);
    if (key.startsWith('step:')) {
      const id = [...index.keys()].find((x) => under(key, `step:${x}`));
      return 10 + (id === undefined ? 0 : (index.get(id) ?? 0));
    }
    const head = key.split('.')[0] ?? '';
    return fixed[head] ?? (head === 'cooldown' ? 10_000 : 20_000);
  };
  return issues
    .map((issue, n) => ({ issue, n, r: rank(issue.loc) }))
    .sort((a, b) => a.r - b.r || a.n - b.n)
    .map((x) => x.issue);
}
